import { Alert, Button, InputNumber, Modal, Space, Switch, Typography } from 'antd';
import { CheckCircleOutlined, RedoOutlined, CloseCircleOutlined } from '@ant-design/icons';
import React from 'react'
import AttestationTPVToPrint from '../../components/docs/AttestationTPVToPrint';
import AttestationTPV from '../../components/docs/AttestationTPV';
import AttestationMotoToPrint from '../../components/docs/AttestationMotoToPrint';
import AttestationMoto from '../../components/docs/AttestationMoto';
import { PrinterOutlined } from '@ant-design/icons';
import { toast } from 'react-toastify';
import AttestationsNumerosInput, { parseAttestationNumbers } from './AttestationsNumerosInput';
import { enginsPourAttestation } from './enginsPourAttestation';
import { aConducteurDifferent } from '../../components/docs/attestationDriver';

const { Title } = Typography;

const ImprimerAttestation = ({
    contrat,
    modalAttOpen,
    setModalAttOpen,
    onTabsChange,
    printingAttestation,
    printAttestation,
    printedAttestation,
    attestationNodeRefs,
    setPrintingAttestation,
    attestationDamaged,
    numeroAttestation,
    setNumeroAttestation,
    loadingPrinting,
    setLoadingPrinting,
    regle,
    rightsService,
    attestationPrinting,
    printingDamagedAttestation,
    attestationNonPrintedPrinting,
    attestationDamagedPrinting,
    layout,
    setLayout,
    setAttestationDamaged,
    printAllAttestations,
    printAllAttestationsRef,
    awaitingBulkConfirm,
    confirmBulkPrintSuccess,
    retryBulkPrint,
    abortBulkPrint
}) => {
    const secondaryColor = "#242166";

    const [numerosRange, setNumerosRange] = React.useState("");
    const { numeros, invalid } = React.useMemo(
        () => parseAttestationNumbers(numerosRange),
        [numerosRange]
    );
    // Liste des engins à imprimer : incorpores pour une incorporation (IN), sinon la flotte.
    const enginsImpr = enginsPourAttestation(contrat);
    const isFlotte = (enginsImpr?.length || 0) > 1;
    // Nom sur l'attestation : mono-engin → ASSURÉ par défaut ; interrupteur pour basculer sur le
    // conducteur habituel s'il est saisi ≠ de l'assuré (flotte : comportement par engin inchangé).
    const [auConducteur, setAuConducteur] = React.useState(false);
    const conducteurDispo = !isFlotte && aConducteurDifferent(contrat, enginsImpr?.[0]);
    const preferSous = isFlotte ? undefined : !auConducteur;
    // Numéro d'attestation INDÉPENDANT par engin (flotte) : sans cela, tous les champs partageaient
    // le même état `numeroAttestation`, si bien que saisir un numéro pour UN engin le répandait sur
    // TOUS les autres. En mono-engin on conserve l'état partagé (comportement inchangé).
    const [numeroParEngin, setNumeroParEngin] = React.useState({});
    const numeroOf = (eng) => (isFlotte ? numeroParEngin[eng?.codeId] : numeroAttestation);
    const setNumeroOf = (eng, v) => {
        if (isFlotte) {
            setNumeroParEngin((prev) => ({ ...prev, [eng?.codeId]: v }));
        } else {
            setNumeroAttestation(v);
        }
    };
    const isTransport = contrat?.engins?.[0]?.categorie?.nom?.toUpperCase()?.startsWith("TRANSPORT");
    const isMoto = contrat?.engins?.[0]?.categorie?.codeCategorie === "M";
    const printAllDisabled =
        numeros.length === 0 ||
        !regle ||
        !rightsService("HCO007") ||
        contrat?.suspendu ||
        contrat?.resilie ||
        contrat?.avenant?.code?.startsWith("RS") ||
        contrat?.avenant?.code?.startsWith("SP");

    return (
        <Modal
            title={
                <Title level={4} style={{ margin: 0, color: secondaryColor }}>
                    <PrinterOutlined style={{ marginRight: 8 }} />
                    Attestation d'Assurance
                </Title>
            }
            centered
            open={modalAttOpen}
            onOk={() => {
                onTabsChange("1");
                setModalAttOpen(false);
            }}
            onCancel={() => {
                onTabsChange("1");
                setModalAttOpen(false);
            }}
            width={1200}
            zIndex={2000}
        >
            {/* ⚠️ TEMPORAIRE — bouton de test : imprime directement l'attestation (version ToPrint,
                avec le badge antidatage) SANS saisir de numéro ni appeler le backend. Sert à ajuster
                le rendu/positionnement du badge. À RETIRER ensuite. */}
            {/* <div style={{ marginBottom: 16, padding: 8, border: "1px dashed #faad14", borderRadius: 8, background: "#fffbe6" }}>
                <span style={{ color: "#ad6800", marginRight: 12, fontWeight: 600 }}>🧪 TEST (provisoire) :</span>
                <Button
                    onClick={() => {
                        setLayout && setLayout("landscape");
                        attestationNonPrintedPrinting && attestationNonPrintedPrinting(contrat?.engins?.[0]);
                    }}
                    icon={<PrinterOutlined />}
                    style={{ background: "#faad14", borderColor: "#faad14", color: "#fff" }}
                >
                    Imprimer l'attestation (sans n°, sans backend)
                </Button>
            </div> */}

            {isFlotte && !awaitingBulkConfirm && (
                <>
                    <AttestationsNumerosInput
                        value={numerosRange}
                        onChange={setNumerosRange}
                        numeros={numeros}
                        invalid={invalid}
                        enginsCount={enginsImpr?.length}
                    />
                    <div style={{ textAlign: "right", marginBottom: 24 }}>
                        <Button
                            type="primary"
                            icon={<PrinterOutlined />}
                            size="large"
                            loading={loadingPrinting}
                            disabled={printAllDisabled}
                            onClick={() => printAllAttestations && printAllAttestations(numeros)}
                            style={{ background: printAllDisabled ? undefined : "green" }}
                        >
                            Imprimer tout ({numeros.length})
                        </Button>
                    </div>
                </>
            )}

            {/* Étape de confirmation : les numéros ne sont attribués qu'après que
                l'utilisateur a vérifié que l'impression est bien sortie. */}
            {isFlotte && awaitingBulkConfirm && (
                <Alert
                    type="warning"
                    showIcon
                    style={{ marginBottom: 24 }}
                    message="L'impression s'est-elle déroulée correctement ?"
                    description={
                        <>
                            <p style={{ marginBottom: 12 }}>
                                Les numéros d'attestation <b>{numeros.join(", ") || "saisis"}</b> ne
                                seront attribués aux engins <b>qu'après votre confirmation</b>. Vérifiez
                                que toutes les attestations sont bien sorties de l'imprimante avant de
                                confirmer.
                            </p>
                            <Space wrap>
                                <Button
                                    type="primary"
                                    icon={<CheckCircleOutlined />}
                                    loading={loadingPrinting}
                                    onClick={() => confirmBulkPrintSuccess && confirmBulkPrintSuccess()}
                                    style={{ background: "green" }}
                                >
                                    Oui, attribuer les numéros
                                </Button>
                                <Button
                                    icon={<RedoOutlined />}
                                    onClick={() => retryBulkPrint && retryBulkPrint()}
                                    disabled={loadingPrinting}
                                >
                                    Réimprimer
                                </Button>
                                <Button
                                    danger
                                    icon={<CloseCircleOutlined />}
                                    onClick={() => abortBulkPrint && abortBulkPrint()}
                                    disabled={loadingPrinting}
                                >
                                    Annuler (aucune attribution)
                                </Button>
                            </Space>
                        </>
                    }
                />
            )}

            {conducteurDispo && (
                <Alert type="info" showIcon style={{ marginBottom: 12 }}
                    message={
                        <Space wrap>
                            <span>Nom sur l'attestation :</span>
                            <Switch checked={auConducteur} onChange={setAuConducteur}
                                checkedChildren="Conducteur" unCheckedChildren="Assuré" />
                            <span style={{ color: '#6b7280' }}>
                                {auConducteur
                                    ? `Conducteur habituel : ${enginsImpr?.[0]?.conducteurHabituel?.nom || ''} ${enginsImpr?.[0]?.conducteurHabituel?.prenom || ''}`.trim()
                                    : `Assuré : ${contrat?.souscripteur?.nom || ''} ${contrat?.souscripteur?.prenom || ''}`.trim()}
                            </span>
                        </Space>
                    }
                />
            )}

            {contrat?.engins?.[0]?.categorie
                ?.nom?.toUpperCase()?.startsWith("TRANSPORT") ? (
                enginsImpr?.map((eng, id) => (
                    <div
                        key={id}
                        style={{
                            marginBottom: isFlotte ? 24 : 0,
                            paddingBottom: isFlotte ? 16 : 0,
                            borderBottom: isFlotte ? "1px dashed #e8e8e8" : "none",
                        }}
                    >
                        {isFlotte && (
                            <div
                                style={{
                                    display: "flex",
                                    alignItems: "baseline",
                                    gap: 8,
                                    marginBottom: 8,
                                }}
                            >
                                <span style={{ fontSize: 13, color: "#888" }}>
                                    Attestation {id + 1}
                                </span>
                                <span
                                    style={{
                                        fontSize: 18,
                                        fontWeight: 700,
                                        color: "#242166",
                                        fontVariantNumeric: "tabular-nums",
                                    }}
                                >
                                    N° {eng.numeroAttestation || (numeroOf(eng) != null ? numeroOf(eng) : (numeros[id] != null ? numeros[id] : "—"))}
                                </span>
                            </div>
                        )}
                        <div className="container" justify="center" >
                            <div
                                className="grid-item"
                                lg={12}
                            >
                                <div
                                    ref={(node) => {
                                        // Enregistre le nœud DE CET engin (indexé sur id) au lieu de
                                        // partager un seul ref qui finirait sur le dernier engin.
                                        if (attestationNodeRefs) attestationNodeRefs.current[id] = node;
                                        // Repli mono-engin : conserve l'ancien comportement.
                                        if (!isFlotte && printedAttestation) printedAttestation.current = node;
                                    }}
                                    style={{
                                        position: "relative",
                                        width: 1080,
                                        height: 560,
                                        overflow: "visible",
                                        pageBreakInside: "avoid",
                                        breakInside: "avoid",
                                    }}
                                >
                                    {printingAttestation ?
                                        <AttestationTPVToPrint
                                            contrat={contrat}
                                            engin={eng}
                                            preferSouscripteur={preferSous}
                                        /> :
                                        <AttestationTPV
                                            ref={printAttestation}
                                            contrat={contrat}
                                            engin={eng}
                                            preferSouscripteur={preferSous}
                                            printing={
                                                printingAttestation
                                            }
                                        />
                                    }
                                </div>
                            </div>
                        </div>
                        {!eng.numeroAttestation || attestationDamaged ? <>
                            {attestationDamaged && (
                                <p style={{ color: "#b45309", fontSize: 13, marginBottom: 8, maxWidth: 360 }}>
                                    Attestation endommagée : saisissez un <b>nouveau numéro encore disponible</b>{" "}
                                    (non imprimé). L'ancien numéro sera automatiquement marqué comme endommagé.
                                </p>
                            )}
                            <InputNumber
                                style={{
                                    width: 200,
                                }}
                                placeholder="Numéro d'attestation"
                                value={numeroOf(eng)}
                                onChange={(v) => {
                                    setNumeroOf(eng, v);
                                }}
                                formatter={(value) =>
                                    `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, " ")
                                }
                                parser={(value) =>
                                    value.replace(/\s/g, "")
                                }
                            />
                            <br />
                            <br />
                            <Button
                                onClick={() => {
                                    setLayout("landscape");
                                    console.log(
                                        "nA: ",
                                        numeroAttestation
                                    );
                                    if (
                                        !numeroOf(eng) || numeroOf(eng) < 1000
                                    ) {
                                        toast.error(
                                            "Veuillez renseigner un numéro d'attestation valide"
                                        );
                                    } else {
                                        setPrintingAttestation(true);
                                        setLoadingPrinting(true);
                                        attestationDamaged ? printingDamagedAttestation(eng, numeroOf(eng)) : attestationPrinting(eng, numeroOf(eng));
                                    }

                                }}
                                type="primary"
                                icon={<PrinterOutlined />}
                                style={{
                                    background: "green",
                                }}
                                disabled={!regle || !rightsService("HCO007") || contrat?.suspendu || contrat?.resilie || contrat?.avenant?.code?.startsWith("RS") || contrat?.avenant?.code?.startsWith("SP")}
                                loading={loadingPrinting}
                            >
                                Imprimer
                            </Button>
                        </> :
                            <>
                                <Button
                                    onClick={() => {
                                        setPrintingAttestation(true);
                                        attestationNonPrintedPrinting(eng);
                                    }}
                                    type="primary"
                                    style={{
                                        background: "gray",
                                        color: "white"
                                    }}
                                    disabled={!regle || !rightsService("HCO007")}
                                >
                                    Impression non effectuée
                                </Button>

                                <Button
                                    onClick={() => {

                                        attestationDamagedPrinting(eng);

                                    }}
                                    type="primary"
                                    style={{
                                        background: "orange",
                                        color: "white"
                                    }}
                                    disabled={!regle || !rightsService("HCO007")}
                                >
                                    Attestation bourrée / endommagée
                                </Button>
                            </>}
                    </div>))
            ) : null}

            {contrat?.engins?.[0]?.categorie
                ?.codeCategorie === "M" ? (
                enginsImpr?.map((eng, id) => (
                    <div
                        key={id}
                        style={{
                            marginBottom: isFlotte ? 24 : 0,
                            paddingBottom: isFlotte ? 16 : 0,
                            borderBottom: isFlotte ? "1px dashed #e8e8e8" : "none",
                        }}
                    >
                        {isFlotte && (
                            <div
                                style={{
                                    display: "flex",
                                    alignItems: "baseline",
                                    gap: 8,
                                    marginBottom: 8,
                                }}
                            >
                                <span style={{ fontSize: 13, color: "#888" }}>
                                    Attestation {id + 1}
                                </span>
                                <span
                                    style={{
                                        fontSize: 18,
                                        fontWeight: 700,
                                        color: "#242166",
                                        fontVariantNumeric: "tabular-nums",
                                    }}
                                >
                                    N° {eng.numeroAttestation || (numeroOf(eng) != null ? numeroOf(eng) : (numeros[id] != null ? numeros[id] : "—"))}
                                </span>
                            </div>
                        )}
                        <div className="container" justify="center" >
                            <div
                                className="grid-item"
                                lg={12}
                            >
                                <div
                                    ref={(node) => {
                                        // Enregistre le nœud DE CET engin (indexé sur id) au lieu de
                                        // partager un seul ref qui finirait sur le dernier engin.
                                        if (attestationNodeRefs) attestationNodeRefs.current[id] = node;
                                        // Repli mono-engin : conserve l'ancien comportement.
                                        if (!isFlotte && printedAttestation) printedAttestation.current = node;
                                    }}
                                    style={{
                                        position: "relative",
                                        width: 1080,
                                        height: 560,
                                        overflow: "visible",
                                        pageBreakInside: "avoid",
                                        breakInside: "avoid",
                                    }}
                                >
                                    {printingAttestation ?
                                        <AttestationMotoToPrint
                                            contrat={contrat}
                                            engin={eng}
                                            preferSouscripteur={preferSous}
                                        /> :
                                        <AttestationMoto
                                            ref={printAttestation}
                                            contrat={contrat}
                                            engin={eng}
                                            preferSouscripteur={preferSous}
                                            printing={
                                                printingAttestation
                                            }
                                        />
                                    }
                                </div>
                            </div>
                        </div>
                        {!eng.numeroAttestation || attestationDamaged ? <>
                            {attestationDamaged && (
                                <p style={{ color: "#b45309", fontSize: 13, marginBottom: 8, maxWidth: 360 }}>
                                    Attestation endommagée : saisissez un <b>nouveau numéro encore disponible</b>{" "}
                                    (non imprimé). L'ancien numéro sera automatiquement marqué comme endommagé.
                                </p>
                            )}
                            <InputNumber
                                style={{
                                    width: 200,
                                }}
                                placeholder="Numéro d'attestation"
                                value={numeroOf(eng)}
                                onChange={(v) => {
                                    setNumeroOf(eng, v);
                                }}
                                formatter={(value) =>
                                    `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, " ")
                                }
                                parser={(value) =>
                                    value.replace(/\s/g, "")
                                }
                            />
                            <br />
                            <br />
                            <Button
                                onClick={() => {
                                    setLayout("landscape");
                                    console.log(
                                        "nA: ",
                                        numeroAttestation
                                    );
                                    if (
                                        !numeroOf(eng) || numeroOf(eng) < 1000
                                    ) {
                                        toast.error(
                                            "Veuillez renseigner un numéro d'attestation valide"
                                        );
                                    } else {
                                        setPrintingAttestation(true);
                                        setLoadingPrinting(true);
                                        attestationDamaged ? printingDamagedAttestation(eng, numeroOf(eng)) : attestationPrinting(eng, numeroOf(eng));
                                    }

                                }}
                                icon={<PrinterOutlined />}
                                type="primary"
                                style={{
                                    background: "green",
                                }}
                                disabled={!regle || !rightsService("HCO007") || contrat?.suspendu || contrat?.resilie || contrat?.avenant?.code?.startsWith("RS") || contrat?.avenant?.code?.startsWith("SP")}
                                loading={loadingPrinting}
                            >
                                Imprimer
                            </Button>
                        </> :
                            <>
                                <Button
                                    onClick={() => {
                                        attestationNonPrintedPrinting(eng);
                                    }}
                                    type="primary"
                                    style={{
                                        background: "gray",
                                        color: "white"
                                    }}
                                    disabled={!regle || !rightsService("HCO007")}
                                >
                                    Impression non effectuée
                                </Button>

                                <Button
                                    onClick={() => {

                                        attestationDamagedPrinting(eng);

                                    }}
                                    type="primary"
                                    style={{
                                        background: "orange",
                                        color: "white"
                                    }}
                                    disabled={!regle || !rightsService("HCO007")}
                                >
                                    Attestation bourrée / endommagée
                                </Button>
                            </>}
                    </div>))
            ) : null}

            {/* Conteneur d'impression groupée de toute la flotte en un seul job.
                NB : on ne peut PAS utiliser display:none NI height:0/overflow:hidden
                (react-to-print n'imprimerait que des pages blanches, ou rognerait le
                bas du contenu). On le place donc simplement hors écran.

                Sur une attestation, certaines écritures cumulent un décalage de colonne
                (top ~270px) ET un marginTop (~270px) : le contenu descend donc jusqu'à
                ~560px (et non 400). On dimensionne chaque page à cette hauteur réelle et
                on INTERDIT la coupure interne (pageBreakInside: avoid) pour qu'aucune
                attestation ne soit scindée à la frontière de page. Saut de page entre
                chaque attestation (mais pas après la dernière → pas de page blanche). */}
            {isFlotte && (isTransport || isMoto) && (
                <div
                    aria-hidden
                    style={{
                        position: "absolute",
                        left: "-99999px",
                        top: 0,
                        width: 1080,
                    }}
                >
                    <div ref={printAllAttestationsRef}>
                        {enginsImpr?.map((eng, id) => {
                            const isLast = id === (enginsImpr?.length || 0) - 1;
                            return (
                                <div
                                    key={id}
                                    style={{
                                        position: "relative",
                                        width: 1080,
                                        height: 580,
                                        overflow: "visible",
                                        pageBreakInside: "avoid",
                                        breakInside: "avoid",
                                        pageBreakAfter: isLast ? "auto" : "always",
                                        breakAfter: isLast ? "auto" : "page",
                                    }}
                                >
                                    {isTransport ? (
                                        <AttestationTPVToPrint contrat={contrat} engin={eng} preferSouscripteur={preferSous} />
                                    ) : (
                                        <AttestationMotoToPrint contrat={contrat} engin={eng} preferSouscripteur={preferSous} />
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}
        </Modal>
    )
}

export default ImprimerAttestation
